import React from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faPen,
  faEye,
} from "@fortawesome/free-solid-svg-icons";
import GreenTick from "../../images/green-tick.png";
import { COUNTRIES } from "../../icons/countryFlag";
import { SkeletonBlock } from "../Skeleton/Skeleton";

/**
 * Profile header (route /profile and /profile/:userId).
 *
 * variant="public"  -> Follow / Message actions
 * variant="private" -> Edit profile / View as public actions
 *
 * Mobile layout is unchanged (vertical stack). Desktop (lg+) uses a wide
 * horizontal card: avatar | identity | leaderboard + actions.
 */

const compactNumber = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(n);
};

const formatTradingSince = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const month = d.toLocaleString("en-US", { month: "short" });
  return `Trading since ${month} ${d.getFullYear()}`;
};

// Display the full country name (no abbreviations). US is shown as "USA";
// every other country uses its full name from the country list, falling back
// to the raw code if it isn't found.
const getCountryName = (code) => {
  if (!code) return null;
  const iso = String(code).toUpperCase();
  if (iso === "US") return "USA";
  const match = COUNTRIES.find((item) => item.iso2 === iso);
  return match ? match.name : iso;
};

const PublicProfileHeader = ({
  profile = {},
  userImg,
  selectedTradingStyle,
  variant = "public",
  only, // "mobile" | "desktop" | undefined (render both)
  onFollow,
  onUnfollow,
  onMessage,
  onLinkClick,
  onEdit,
  onViewAsPublic,
  isDisabled = false,
  // False while the live profile is still in flight. The rank and the meta strip
  // then render as skeleton blocks instead of asserting something.
  //
  // This matters because "Unranked" is a STATEMENT OF FACT, not a placeholder:
  // showing it and then correcting to "#42" tells the user they are unranked
  // when we simply don't know yet. Same for the meta strip, which would
  // otherwise grow from "Trading since Jan 2024" to "… · 126 trades · 11.2K
  // fans" as the counts arrive. Defaults true so existing callers are unchanged.
  rankReady = true,
  // Client League Play Profiles spec, item 7: the rank label is per-context.
  // Sitewide it is the site leaderboard; inside a league it is that league's own
  // rank ("LEAGUE RANK"), and inside a cash game it is "GAME RANK". Defaults to
  // the sitewide wording so /profile and /profile/:userId are unchanged.
  rankLabel = "LEADERBOARD",
  // Client League Play Profiles spec, item 4: on a League Play profile the
  // OWNER (the person whose profile it is) must not see "Edit profile" or
  // "View as public" — those belong to the main QUSHi profile, and tapping them
  // takes the member out of the league. The main app keeps both.
  showOwnerActions = true,
  // Where "View QUSHi Record ›" points, or null for "do not show it".
  //
  // Client, League Play Dev Request, "League Profile Routing":
  //   Add View QUSHi Record › directly beneath Follow and Message and before the
  //   League Record card.
  //   Show View QUSHi Record on another trader's league profile whether it was
  //   opened from a live or completed leaderboard.
  //   View QUSHi Record must open the trader's main public QUSHi profile.
  //
  // A prop rather than a rule computed in here, because this component is also
  // the MAIN profile's header, where the link would point at the page you are
  // already on. Profile.jsx decides (it is the only caller that knows whether a
  // game is in play and whose profile this is); this file only renders.
  qushiRecordHref = null,
}) => {
  const isPrivate = variant === "private";
  const username = profile?.user_name || profile?.full_name || "";
  const countryCode = getCountryName(profile?.country_code);
  const traderType = selectedTradingStyle ? selectedTradingStyle : null;
  const isFollowing = profile?.is_follow_by_me;
  const link = profile?.link && profile.link !== "null" ? profile.link : null;
  const rank = profile?.leaderboard_rank;

  // Country + trader type collapse into one quiet dot-strip under the username
  // ("USA · Swing Trader") — no icons, natural casing, matching the meta strip.
  const identityStrip = [countryCode, traderType].filter(Boolean).join(" · ");

  // Meta line: "Trading since Jan 2024 · 126 trades · 11.2K fans"
  // "Trading since" tracks the CURRENT run's start, not the account's birth. A
  // portfolio reset — either the in-app "The Comeback" or a backend reset (both
  // go through Helper::resetUserRecord) — stamps users.last_reset and wipes the
  // trades/performance history. So use last_reset when present (matches the
  // reset-aware performance chart and trades count) and fall back to created_at
  // only for accounts that have never reset. Guard the "null" string the way
  // other fields here do (e.g. link).
  const metaParts = [];
  const resetAt =
    profile?.last_reset && profile.last_reset !== "null"
      ? profile.last_reset
      : null;
  const since = formatTradingSince(resetAt || profile?.created_at);
  if (since) metaParts.push(since);
  if (profile?.trades_count != null) {
    metaParts.push(`${compactNumber(profile.trades_count)} trades`);
  }
  if (profile?.follower_count != null) {
    metaParts.push(
      `${compactNumber(profile.follower_count)} ${
        Number(profile.follower_count) === 1 ? "fan" : "fans"
      }`
    );
  }
  const metaLine = metaParts.join(" · ");

  const renderActions = (mobile = false) =>
    isPrivate && !showOwnerActions ? null : isPrivate ? (
      mobile ? (
        // Mobile private: plain text links (no boxes) per design.
        <div
          id={only === "mobile" ? "profile-guide-edit-view-mobile" : "profile-guide-edit-view"}
          className="flex items-center gap-5"
        >
          <button
            type="button"
            onClick={() => onEdit && onEdit()}
            className="text-secondary font-normal font-poppins text-sm"
          >
            Edit profile
          </button>
          <button
            type="button"
            onClick={() => onViewAsPublic && onViewAsPublic()}
            className="text-secondary font-normal font-poppins text-sm"
          >
            View as public
          </button>
        </div>
      ) : (
        <div className="flex gap-3 mt-5">
          <button
            type="button"
            onClick={() => onEdit && onEdit()}
            className="px-8 py-2.5 rounded-lg bg-white text-[#173E3B] font-semibold font-poppins border border-[#173E3B]"
          >
            Edit profile
          </button>
          <button
            type="button"
            onClick={() => onViewAsPublic && onViewAsPublic()}
            className="px-8 py-2.5 rounded-lg bg-white text-[#173E3B] font-semibold font-poppins border border-gray-300"
          >
            View as public
          </button>
        </div>
      )
    ) : (
      <div className="flex gap-3 mt-5">
        {isFollowing ? (
          <button
            type="button"
            disabled={isDisabled}
            onClick={() => onUnfollow && onUnfollow(profile?.uuid)}
            className="px-4 py-1.5 rounded-lg bg-primaryColor text-white font-semibold font-poppins border border-primaryColor disabled:opacity-60"
          >
            Following
          </button>
        ) : (
          <button
            type="button"
            disabled={isDisabled}
            onClick={() => onFollow && onFollow(profile?.uuid)}
            className="px-4 py-1.5 rounded-lg bg-white text-primaryColor font-semibold font-poppins border border-primaryColor disabled:opacity-60 w-max"
          >
            Follow
          </button>
        )}
        <button
          type="button"
          onClick={() => onMessage && onMessage(profile)}
          className="px-4 py-1.5 rounded-lg bg-white text-[#374151] font-semibold font-poppins border border-[#D1D5DB] w-max"
        >
          Message
        </button>
      </div>
    );

  // "directly beneath Follow and Message" — its own row under the button pair,
  // not a third button beside them: this is a way OUT of League Play to the
  // trader's sitewide record, which is a different kind of action from following
  // or messaging them inside this league.
  const qushiRecordLink =
    !isPrivate && qushiRecordHref ? (
      <Link
        to={qushiRecordHref}
        className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#0F4146] hover:underline"
      >
        View QUSHi Record ›
      </Link>
    ) : null;

  const avatar = (sizeClasses) => (
    <div className="relative shrink-0">
    {/* <div className="bg-white p-1 rounded-full absolute bottom-0 right-0"><img src="/circle-check-icon.svg" alt="circle check" /></div> */}
      {userImg ? (
        <img
          src={userImg}
          alt={username}
          className={`${sizeClasses} rounded-full object-cover shadow-xl`}
        />
      ) : (
        <div
          className={`${sizeClasses} rounded-full bg-gray-200 shadow-xl flex items-center justify-center`}
        >
          <FontAwesomeIcon icon={faUsers} className="text-gray-400 text-2xl" />
        </div>
      )}
      {profile?.green_tick && (
        <img
          src={GreenTick}
          alt="verified"
          className="absolute bottom-0 right-0 w-6 h-6 rounded-full bg-white"
        />
      )}
    </div>
  );

  const linkEl = (extraClass = "") =>
    link ? (
      <a
        href={link.startsWith("http") ? link : `https://${link}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => onLinkClick && onLinkClick(link)}
        className={`flex items-center gap-2 font-semibold text-sm break-all ${extraClass}`}
      >
        <img
          src="/link-icon.svg"
          alt="link-icon"
          className="w-5"
        />
        <span className="text-primaryColor">{link}</span>
      </a>
    ) : null;

  return (
    <>
      {/* ===================== MOBILE (unchanged) ===================== */}
      {only !== "desktop" && (
      <div className="lg:hidden flex flex-col gap-4 px-4 pt-0">
        {/* Identity */}
        <div className="flex items-start gap-4 mb-3">
          {avatar("w-20 h-20")}
          <div className="flex flex-col min-w-0 pt-1 gap-1">
            <h1 className="text-[22px] font-bold font-poppins text-[#1A1A1A] leading-tight break-words">
              {username}
            </h1>
            {identityStrip && (
              <p className="text-[#8A8B8F] text-sm mt-1 font-medium">
                {identityStrip}
              </p>
            )}
          </div>
        </div>

        {((profile?.title && profile.title !== "null") ||
          (profile?.biography && profile.biography !== "null")) && (
          <div>
            {profile?.biography && profile.biography !== "null" && (
              <p className="text-[15px] text-[#4A4A4A] font-inter leading-relaxed whitespace-pre-line">
                {profile.biography}
              </p>
            )}
            {profile?.title && profile.title !== "null" && (
              <p className="text-[15px] text-[#4A4A4A] font-inter leading-relaxed">
                {profile.title}
              </p>
            )}
          </div>
        )}

        {linkEl()}

        {/* Part 2 */}
        <div className="border-t border-[#F3F4F6] py-5 my-2 flex flex-col gap-4">
          <div>
            <p className="text-3xl leading-none font-semibold font-poppins text-[#D7C588]">
              {!rankReady ? (
                      <SkeletonBlock height="0.8em" width="3.2em" />
                    ) : rank != null ? (
                      `#${rank}`
                    ) : (
                      "Unranked"
                    )}
            </p>
            <p className="mt-1.5 text-[12px] tracking-[0.18em] text-[#A0A3A8] font-semibold">
              {rankLabel}
            </p>
          </div>
          {!rankReady ? (
            <SkeletonBlock height={14} width="80%" />
          ) : (
            metaLine && (
              <p className="text-[14px] text-[#6B7280] font-inter font-medium">{metaLine}</p>
            )
          )}
          {renderActions(true)}
          {qushiRecordLink}
        </div>
      </div>
      )}

      {/* ===================== DESKTOP (lg+) ===================== */}
      {only !== "mobile" && (
      <div
        className={`hidden lg:block mb-4 lg:mb-0 ${
          isPrivate ? "lg:pb-8" : "lg:p-8 lg:pb-0"
        }`}
      >
        <div className="rounded-2xl border border-[#F3F4F6] p-6 relative">
          {isPrivate && showOwnerActions && (
            <div id="profile-guide-edit-view" className="absolute top-5 right-5 flex items-center gap-5">
              <button
                type="button"
                onClick={() => onEdit && onEdit()}
                title="Edit profile"
                className="text-[#8A8B8F] hover:text-[#173E3B]"
              >
                <FontAwesomeIcon icon={faPen} />
              </button>
              <button
                type="button"
                onClick={() => onViewAsPublic && onViewAsPublic()}
                title="View as public"
                className="text-[#8A8B8F] hover:text-[#173E3B]"
              >
                <FontAwesomeIcon icon={faEye} />
              </button>
            </div>
          )}
          <div className="flex items-start gap-6">
            {avatar("w-24 h-24")}

            {/* Identity — mobile order: username, strip, bio, link */}
            <div className="flex-1 min-w-0">
              <h1 className="text-[26px] font-bold font-poppins text-[#1A1A1A] leading-tight break-words">
                {username}
              </h1>
              {identityStrip && (
                <p className="mt-2 text-sm text-[#8A8B8F] font-medium">
                  {identityStrip}
                </p>
              )}
              {((profile?.title && profile.title !== "null") ||
                (profile?.biography && profile.biography !== "null")) && (
                <div className="mt-4">
                  {profile?.biography && profile.biography !== "null" && (
                    <p className="text-[15px] text-[#4A4A4A] font-inter leading-relaxed whitespace-pre-line">
                      {profile.biography}
                    </p>
                  )}
                  {profile?.title && profile.title !== "null" && (
                    <p className="text-[15px] text-[#4A4A4A] font-inter leading-relaxed">
                      {profile.title}
                    </p>
                  )}
                </div>
              )}
              {link && <div className="mt-4">{linkEl()}</div>}
            </div>

            {/* Leaderboard + actions (public: header right) */}
            {!isPrivate && (
              <div className="shrink-0 flex flex-col items-start gap-3 min-w-[335px]">
                <div>
                  <p className="text-[34px] leading-none font-extrabold font-poppins text-[#C5A253]">
                    {!rankReady ? (
                      <SkeletonBlock height="0.8em" width="3.2em" />
                    ) : rank != null ? (
                      `#${rank}`
                    ) : (
                      "Unranked"
                    )}
                  </p>
                  <p className="mt-1.5 text-[12px] tracking-[0.18em] text-[#A0A3A8] font-semibold">
                    {rankLabel}
                  </p>
                </div>
                {!rankReady ? (
                  <SkeletonBlock height={14} width={260} />
                ) : (
                  metaLine && (
                    <p className="text-[14px] text-[#8A8B8F] font-inter font-medium">
                      {metaLine}
                    </p>
                  )
                )}
                {renderActions()}
                {qushiRecordLink}
              </div>
            )}
          </div>

          {/* Private: leaderboard + meta inside the same header card */}
          {isPrivate && (
            <div className="mt-5 pt-5 border-t border-gray-100">
              <p className="text-[34px] leading-none font-extrabold font-poppins text-[#C5A253]">
                {!rankReady ? (
                      <SkeletonBlock height="0.8em" width="3.2em" />
                    ) : rank != null ? (
                      `#${rank}`
                    ) : (
                      "Unranked"
                    )}
              </p>
              <p className="mt-1.5 text-[12px] tracking-[0.18em] text-[#A0A3A8] font-semibold">
                {rankLabel}
              </p>
              {!rankReady ? (
                <SkeletonBlock height={14} width={300} className="mt-3" />
              ) : (
                metaLine && (
                  <p className="text-[14px] text-[#8A8B8F] font-inter mt-2 font-medium">
                    {metaLine}
                  </p>
                )
              )}
            </div>
          )}
        </div>
      </div>
      )}
    </>
  );
};

export default PublicProfileHeader;
